const PRODUCT_CAROUSEL_SELECTOR = 'slideshow-component.resource-list__carousel';
const PRODUCT_CARD_SELECTOR = 'product-card, .product-card';
const CARD_GALLERY_SELECTOR = '.card-gallery';
const SCROLL_TOLERANCE = 1;

const observedCarousels = new WeakSet();
const observedMedia = new WeakSet();
const syncFrames = new WeakMap();
const carouselResizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const carousel = entry.target.matches(PRODUCT_CAROUSEL_SELECTOR)
      ? entry.target
      : entry.target.closest(PRODUCT_CAROUSEL_SELECTOR);

    scheduleCarouselSync(carousel);
  }
});

function isProductCarousel(carousel) {
  return carousel.matches(PRODUCT_CAROUSEL_SELECTOR) && carousel.querySelector(PRODUCT_CARD_SELECTOR);
}

function scheduleCarouselSync(carousel) {
  if (!(carousel instanceof HTMLElement) || !isProductCarousel(carousel)) return;

  const pendingFrame = syncFrames.get(carousel);
  if (pendingFrame) {
    cancelAnimationFrame(pendingFrame);
  }

  const frame = requestAnimationFrame(() => {
    const secondFrame = requestAnimationFrame(() => syncCarouselControls(carousel));
    syncFrames.set(carousel, secondFrame);
  });

  syncFrames.set(carousel, frame);
}

function syncCarouselControls(carousel) {
  syncFrames.delete(carousel);

  if (!carousel.isConnected || !isProductCarousel(carousel)) return;

  const scroller = carousel.querySelector('slideshow-slides');
  if (!(scroller instanceof HTMLElement)) return;

  const hasOverflow = scroller.scrollWidth > scroller.clientWidth + SCROLL_TOLERANCE;
  carousel.dataset.sealProductCarouselScrollable = hasOverflow ? 'true' : 'false';
  syncCarouselMedia(carousel);
}

function markGalleryLoaded(gallery) {
  if (!(gallery instanceof HTMLElement)) return;

  gallery.dataset.sealMediaLoaded = 'true';
}

function observeGalleryMedia(gallery) {
  if (!(gallery instanceof HTMLElement) || observedMedia.has(gallery)) return;

  observedMedia.add(gallery);

  const image = gallery.querySelector('img');
  if (!(image instanceof HTMLImageElement)) {
    markGalleryLoaded(gallery);
    return;
  }

  if (image.complete) {
    markGalleryLoaded(gallery);
    return;
  }

  image.addEventListener('load', () => markGalleryLoaded(gallery), { once: true });
  image.addEventListener('error', () => markGalleryLoaded(gallery), { once: true });
}

function syncCarouselMedia(carousel) {
  if (!(carousel instanceof HTMLElement)) return;

  carousel.querySelectorAll(CARD_GALLERY_SELECTOR).forEach(observeGalleryMedia);
}

function observeCarousel(carousel) {
  if (!(carousel instanceof HTMLElement) || !isProductCarousel(carousel)) return;

  if (observedCarousels.has(carousel)) {
    scheduleCarouselSync(carousel);
    syncCarouselMedia(carousel);
    return;
  }

  observedCarousels.add(carousel);
  carousel.dataset.sealProductCarouselScrollable = 'false';
  carouselResizeObserver.observe(carousel);

  const scroller = carousel.querySelector('slideshow-slides');
  if (scroller instanceof HTMLElement) {
    carouselResizeObserver.observe(scroller);
  }

  scheduleCarouselSync(carousel);
  syncCarouselMedia(carousel);
}

function observeProductCarousels(root = document) {
  if (root instanceof HTMLElement) {
    if (isProductCarousel(root)) {
      observeCarousel(root);
    }

    const closestCarousel = root.closest(PRODUCT_CAROUSEL_SELECTOR);
    if (closestCarousel) {
      observeCarousel(closestCarousel);
    }
  }

  root.querySelectorAll?.(PRODUCT_CAROUSEL_SELECTOR).forEach(observeCarousel);
}

observeProductCarousels();

new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) {
        observeProductCarousels(node);
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

document.addEventListener('seal:product-carousel:updated', (event) => {
  if (!(event.target instanceof HTMLElement)) return;

  observeProductCarousels(event.target);
});
